import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  RotateCcw,
  HandHeart,
  Heart,
  ListChecks,
  PackageCheck,
  PlayCircle,
} from "lucide-react";import useActivity from "@/hooks/useActivity";
import useSeo from "@/hooks/useSeo";
import { useApp } from "@/context/AppContext";
import { getVolunteerProgress, setVolunteerProgress, resetVolunteer, incrementKitCount, resetKitCount } from "@/lib/storage";
import ProgressBar from "@/components/activity/ProgressBar";
import Callout from "@/components/activity/Callout";
import ConfettiBurst from "@/components/activity/ConfettiBurst";
import PencilSharpenerIcon from "@/components/activity/PencilSharpenerIcon";
import EraserIcon from "@/components/activity/EraserIcon";
import { LoadingState, ErrorState, NotFoundState } from "@/components/common/States";

export default function VolunteerFlow() {
  const { slug } = useParams();
  const { activity, status } = useActivity(slug);
  const { role, setRole } = useApp();
  useSeo("Volunteer Guide", "Follow the guided steps to pack and personalize a complete kit - no briefing needed.");

  useEffect(() => {
    if (role !== "volunteer") setRole("volunteer");
  }, [role, setRole]);

  const [progress, setProgress] = useState(() => getVolunteerProgress(slug));

  // Transient (never persisted) flag: true only for the instant the volunteer
  // has just completed the flow, so the completion confetti fires exactly
  // once per genuine finish - not on page reload or when simply revisiting
  // an already-completed "You Did It!" screen from persisted progress.
  const [justFinished, setJustFinished] = useState(false);

  // Persist on every change.
  useEffect(() => {
    setVolunteerProgress(slug, progress);
  }, [slug, progress]);

  const flow = activity?.volunteerFlow;
  const repeat = flow?.repeatKit || null;
  const N = repeat ? repeat.ratio : 0;
  const messages = repeat ? repeat.messages : [];

  const stages = useMemo(() => {
    if (!flow) return [];
    const list = [{ type: "welcome" }, { type: "why" }];
    // Some activities (e.g. supply-drive kits built progressively) have no
    // single "gather" step - only include it when there are materials to list.
    if (flow.gatherMaterials && flow.gatherMaterials.length > 0) {
      list.push({ type: "gather" });
    }
    flow.steps.forEach((s) => list.push({ type: "step", step: s }));
    list.push({ type: "quality" });
    // Some activities split packing instructions into their own dedicated
    // page (structured sections) rather than a single dense paragraph.
    if (flow.packUp) list.push({ type: "packup" });
    // Repeat-kit activities replace the single "done" screen with a kit-counter loop.
    if (!flow.repeatKit) list.push({ type: "done" });
    return list;
  }, [flow]);

  const firstStepIndex = useMemo(() => stages.findIndex((s) => s.type === "step"), [stages]);
  const index = Math.min(progress.stepIndex || 0, Math.max(0, stages.length - 1));

  const goTo = useCallback((i) => {
    setProgress((p) => ({ ...p, stepIndex: Math.max(0, i) }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const toggleGather = useCallback((i) => {
    setProgress((p) => {
      const gathered = { ...(p.gathered || {}) };
      if (gathered[i]) delete gathered[i];
      else gathered[i] = true;
      return { ...p, gathered };
    });
  }, []);

  const toggleQuality = useCallback((i) => {
    setProgress((p) => {
      const quality = { ...(p.quality || {}) };
      if (quality[i]) delete quality[i];
      else quality[i] = true;
      return { ...p, quality };
    });
  }, []);

  const markStepDone = useCallback((stepNumber, nextIndex) => {
    setProgress((p) => {
      const done = { ...(p.done || {}), [stepNumber]: true };
      return { ...p, done, stepIndex: nextIndex };
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ---- Repeat-kit loop handlers -------------------------------------------
  const completeKit = useCallback(() => {
    incrementKitCount(slug); // all-time, per-activity counter
    let willFinish = false;
    setProgress((p) => {
      const completed = (p.completedKits || 0) + 1;
      const screen = completed >= N ? "final" : "kitComplete";
      willFinish = completed >= N;
      return { ...p, completedKits: completed, screen };
    });
    if (willFinish) setJustFinished(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug, N]);

  const packAnotherKit = useCallback(() => {
    setJustFinished(false);
    setProgress((p) => ({ ...p, screen: null, stepIndex: firstStepIndex, done: {}, gathered: {}, quality: {} }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [firstStepIndex]);

  const skipToFinal = useCallback(() => {
    setJustFinished(true);
    setProgress((p) => ({ ...p, screen: "final" }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  function handleReset() {
    if (window.confirm("Start over from the beginning? Your ticked items will be cleared.")) {
      resetVolunteer(slug);
      resetKitCount(slug);
      setJustFinished(false);
      setProgress({ stepIndex: 0, done: {}, gathered: {}, quality: {}, completedKits: 0, screen: null });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  if (status === "loading") return <LoadingState />;
  if (status === "notfound") return <NotFoundState />;
  if (status === "error" || !activity || !flow) return <ErrorState onRetry={() => window.location.reload()} />;

  const stage = stages[index];
  const pct = (index / (stages.length - 1)) * 100;
  const isLast = index === stages.length - 1;
  const onLoopScreen = !!(repeat && (progress.screen === "kitComplete" || progress.screen === "final"));
  const completed = progress.completedKits || 0;

  const headerLabel = onLoopScreen ? "Complete" : isLast ? "Complete" : `Step ${index + 1} of ${stages.length}`;
  const barValue = onLoopScreen || isLast ? 100 : pct;
  const showNav = !onLoopScreen && stage.type !== "done";

  return (
    <div className="min-h-[70vh]">
      {/* Sticky progress header */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-brand-navy/10">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3 mb-2">
            <Link
              to={`/activities/${slug}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-navy/60 hover:text-brand-orange"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {activity.name}
            </Link>
            <span className="text-xs font-semibold text-brand-navy/60">{headerLabel}</span>
          </div>
          <ProgressBar value={barValue} />
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        {onLoopScreen ? (
          <div
            className="text-center rounded-3xl bg-brand-navy text-white p-8 sm:p-12 relative overflow-hidden animate-pop"
            data-testid={progress.screen === "final" ? "done-screen" : "kit-complete-screen"}
          >
            {progress.screen === "final" && justFinished && <ConfettiBurst />}
            <div className="relative z-10 text-7xl mb-4 animate-pop" aria-hidden="true">
              {progress.screen === "final" ? "\uD83C\uDF89" : "\u2705"}
            </div>
            <h2 className="relative z-10 display-title text-3xl sm:text-4xl" data-testid="kit-loop-heading">
              {progress.screen === "final" ? flow.done.title : `Kit ${completed} of ${N} done!`}
            </h2>
            <p className="relative z-10 mt-3 max-w-md mx-auto text-white/90 text-lg" data-testid="kit-loop-message">
              {progress.screen === "final" ? messages[N - 1] : messages[Math.max(0, completed - 1)]}
            </p>
            {progress.screen !== "final" && (
              <div className="relative z-10 mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  data-testid="pack-another-kit"
                  onClick={packAnotherKit}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[48px] px-6 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" /> Pack Another Kit
                </button>
                {/* Shortcut to the final screen - available on every intermediate
                    "Kit X of N done!" screen, starting after Kit 1. */}
                <button
                  data-testid="finish-all-kits"
                  onClick={skipToFinal}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white/15 hover:bg-white/25 text-white font-semibold min-h-[48px] px-6 transition-colors"
                >
                  <Check className="w-4 h-4" /> I've packed all {N} kits
                </button>
              </div>
            )}
          </div>
        ) : (
          <div key={index} className="animate-fade-up">
            {stage.type === "welcome" && (
              <StageCard icon={HandHeart} eyebrow="Volunteer Guide" title={flow.welcome.title}>
                <p className="text-lg text-brand-navy/80 leading-relaxed">{flow.welcome.body}</p>
              </StageCard>
            )}

            {stage.type === "why" && (
              <StageCard icon={Heart} eyebrow="A quick why" title={flow.why.title}>
                <p className="text-lg text-brand-navy/80 leading-relaxed">{flow.why.body}</p>
              </StageCard>
            )}

            {stage.type === "gather" && (
              <StageCard icon={PackageCheck} eyebrow="Get ready" title={flow.gatherTitle || "Gather your materials"}>
                <p className="text-brand-navy/70 mb-4">{flow.gatherIntro || "Everything is on the table. Tick each as you collect it."}</p>
                {flow.gatherNote && (
                  <p className="text-sm text-brand-navy/80 bg-brand-cream border border-brand-orange/30 rounded-xl p-3 mb-4" data-testid="gather-note">{flow.gatherNote}</p>
                )}
                <ul className="space-y-2.5" data-testid="gather-list">
                  {flow.gatherMaterials.map((m, i) => {
                    const checked = !!progress.gathered?.[i];
                    return (
                      <li key={i}>
                        <button
                          data-testid={`gather-item-${i}`}
                          onClick={() => toggleGather(i)}
                          className={`w-full flex items-center gap-3 rounded-2xl border p-3.5 min-h-[44px] text-left transition-colors ${
                            checked ? "bg-brand-cream border-brand-orange/40" : "bg-white border-brand-navy/10 hover:border-brand-orange/40"
                          }`}
                        >
                          <span className={`flex-shrink-0 w-7 h-7 rounded-lg border-2 flex items-center justify-center ${checked ? "bg-brand-orange border-brand-orange" : "border-brand-navy/25"}`}>
                            {checked && <Check className="w-5 h-5 text-white" strokeWidth={3} />}
                          </span>
                          <span className={`flex-1 text-[15px] ${checked ? "text-brand-navy/60 line-through" : "text-brand-navy"}`}>
                            {m.instruction}
                          </span>
                          <span className="flex-shrink-0 font-display text-lg text-brand-orange">{m.qty}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </StageCard>
            )}

            {stage.type === "step" && (
              <StageCard
                icon={null}
                eyebrow={`Step ${stage.step.number} of ${flow.steps.length}`}
                title={stage.step.title}
                bigNumber={stage.step.number}
              >
                {/* Illustration placeholder */}
                <div className="rounded-2xl bg-brand-cream h-40 sm:h-48 flex items-center justify-center mb-4 overflow-hidden">
                  {stage.step.image ? (
                    <img
                      src={stage.step.image}
                      alt={stage.step.title}
                      className="w-full h-full object-contain p-2 rounded-xl"
                      loading="lazy"
                    />
                  ) : stage.step.title === "Pencil Sharpener" ? (
                    <PencilSharpenerIcon className="w-20 h-20 sm:w-24 sm:h-24" />
                  ) : stage.step.title === "Eraser" ? (
                    <EraserIcon className="w-20 h-20 sm:w-24 sm:h-24" />
                  ) : (
                    <span className="text-6xl" aria-hidden="true">{stage.step.emoji || ["\u270D\uFE0F", "\u2764\uFE0F", "\u270F\uFE0F", "\u2B50", "\uD83D\uDCE6", "\uD83C\uDF80"][stage.step.number - 1] || "\u2728"}</span>
                  )}
                </div>
                <p className="text-lg text-brand-navy/85 leading-relaxed">{stage.step.body}</p>
                {stage.step.videoUrl && (
                  <a
                    href={stage.step.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`step-video-link-${stage.step.number}`}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/30 px-3.5 py-1.5 text-sm font-semibold text-brand-orange underline underline-offset-2 hover:bg-brand-orange hover:text-white hover:no-underline transition-colors"
                  >
                    <PlayCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" /> Tap here for video instructions
                  </a>
                )}
                {stage.step.tip && <div className="mt-4"><Callout>{stage.step.tip}</Callout></div>}
                {progress.done?.[stage.step.number] && (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-orange">
                    <Check className="w-4 h-4" /> Marked done
                  </p>
                )}
              </StageCard>
            )}

            {stage.type === "quality" && (
              <StageCard icon={ListChecks} eyebrow="Almost there" title="Final quality check">
                <p className="text-brand-navy/70 mb-4">{flow.qualityIntro || "Check the bag has everything before you drop it off."}</p>
                <ul className="space-y-2.5" data-testid="quality-list">
                  {flow.qualityChecklist.map((q, i) => {
                    const checked = !!progress.quality?.[i];
                    return (
                      <li key={i}>
                        <button
                          data-testid={`quality-item-${i}`}
                          onClick={() => toggleQuality(i)}
                          className={`w-full flex items-center gap-3 rounded-2xl border p-3.5 min-h-[44px] text-left transition-colors ${
                            checked ? "bg-brand-cream border-brand-orange/40" : "bg-white border-brand-navy/10 hover:border-brand-orange/40"
                          }`}
                        >
                          <span className={`flex-shrink-0 w-7 h-7 rounded-lg border-2 flex items-center justify-center ${checked ? "bg-brand-orange border-brand-orange" : "border-brand-navy/25"}`}>
                            {checked && <Check className="w-5 h-5 text-white" strokeWidth={3} />}
                          </span>
                          <span className={`flex-1 text-[15px] ${checked ? "text-brand-navy/60 line-through" : "text-brand-navy"}`}>{q}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {flow.qualityNote && (
                  <div className="mt-4">
                    <Callout title="Then pack up">{flow.qualityNote}</Callout>
                  </div>
                )}
              </StageCard>
            )}

            {stage.type === "packup" && (
              <StageCard icon={PackageCheck} eyebrow="Almost there" title={flow.packUp.title}>
                <div className="space-y-4" data-testid="packup-sections">
                  {flow.packUp.sections.map((section, i) => (
                    <div
                      key={i}
                      data-testid={`packup-section-${i}`}
                      className="rounded-2xl bg-brand-cream border border-brand-navy/10 p-4 sm:p-5"
                    >
                      <p className="font-semibold text-brand-navy leading-snug">{section.heading}</p>
                      {section.items && section.items.length > 0 && (
                        <ul className="mt-3 space-y-2">
                          {section.items.map((item, j) => (
                            <li key={j} className="flex gap-2.5 text-[15px] text-brand-navy/85 leading-snug">
                              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-orange mt-2" aria-hidden="true" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </StageCard>
            )}

            {stage.type === "done" && (
              <div className="text-center rounded-3xl bg-brand-navy text-white p-8 sm:p-12 relative overflow-hidden animate-pop" data-testid="done-screen">
                {justFinished && <ConfettiBurst />}
                <div className="relative z-10 text-7xl mb-4 animate-pop" aria-hidden="true">{"\uD83C\uDF89"}</div>
                <h2 className="relative z-10 display-title text-3xl sm:text-4xl">{flow.done.title}</h2>
                <p className="relative z-10 mt-3 max-w-md mx-auto text-white/90 text-lg">{flow.done.body}</p>
                <button
                  data-testid="volunteer-restart"
                  onClick={handleReset}
                  className="relative z-10 mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[48px] px-6 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" /> Start another kit
                </button>
              </div>
            )}
          </div>
        )}

        {/* Navigation controls */}
        {showNav && (
          <div className="mt-8 flex items-center justify-between gap-3">
            <button
              data-testid="vol-back"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
              className="inline-flex items-center gap-2 rounded-full border-2 border-brand-navy/15 text-brand-navy font-semibold min-h-[48px] px-5 disabled:opacity-40 hover:border-brand-orange transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            {stage.type === "step" ? (
              <button
                data-testid="vol-mark-done"
                onClick={() => markStepDone(stage.step.number, index + 1)}
                className="inline-flex items-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[48px] px-6 transition-colors"
              >
                <Check className="w-4 h-4" /> Mark done
              </button>
            ) : (
              <button
                data-testid="vol-next"
                onClick={() => {
                  if (stage.type === "quality") {
                    if (repeat) {
                      completeKit();
                    } else if (flow.packUp) {
                      goTo(index + 1); // move to the dedicated Pack Up Your Kit page - not finished yet
                    } else {
                      setJustFinished(true);
                      goTo(index + 1);
                    }
                  } else if (stage.type === "packup") {
                    setJustFinished(true);
                    goTo(index + 1); // this is the real completion point
                  } else {
                    goTo(index + 1);
                  }
                }}
                className="inline-flex items-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[48px] px-6 transition-colors"
              >
                {stage.type === "quality"
                  ? (flow.packUp ? "Let's Pack It Up" : "Finish")
                  : stage.type === "packup"
                  ? "Finish"
                  : "Continue"} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between gap-3 border-t border-brand-navy/10 pt-5">
          <button
            data-testid="vol-reset"
            onClick={handleReset}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy/60 hover:text-brand-orange"
          >
            <RotateCcw className="w-4 h-4" /> Reset progress
          </button>
        </div>
      </div>
    </div>
  );
}

function StageCard({ icon: Icon, eyebrow, title, bigNumber, children }) {
  return (
    <div className="rounded-3xl bg-white border border-brand-navy/10 pib-shadow p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-3">
        {bigNumber ? (
          <div className="w-12 h-12 rounded-xl bg-brand-orange text-white font-display text-2xl flex items-center justify-center">
            {bigNumber}
          </div>
        ) : Icon ? (
          <div className="w-12 h-12 rounded-xl bg-brand-orange/12 flex items-center justify-center">
            <Icon className="w-6 h-6 text-brand-orange" />
          </div>
        ) : null}
        <div>
          {eyebrow && <p className="text-xs font-bold uppercase tracking-wide text-brand-orange">{eyebrow}</p>}
          <h2 className="display-title text-2xl sm:text-3xl text-brand-navy leading-tight">{title}</h2>
        </div>
      </div>
      {children}
    </div>
  );
}
